/**
 * What "on the price list" means — the ONE definition, shared by the generator
 * and the Products Spreadsheet.
 *
 * Since 2026-08-24 it is a plain checkbox, `metadata.priceList`. Before that it
 * was read off the `addToPriceList` coded select, whose stored values carry a
 * numeric prefix (`1 - Globally`), so a plain `=== 'Globally'` silently matched
 * nothing. That trap is what the checkbox removes.
 */
/**
 * The `addToPriceList` codes that seeded the checkbox at backfill.
 *
 * Kept for the migration and for anyone reconciling the two, NOT as a gate:
 * nothing reads these to decide membership any more. `3 - EU Only` was
 * deliberately excluded — EU Only is being deprecated, and the EU/UK workbook
 * has always selected on `euPriceList = Yes` instead.
 *
 * Counted against production 2026-08-21: 5,165 products matched.
 */
export const US_PRICE_LIST_VALUES = ['1 - Globally', '2 - US Only'];
/**
 * True when the product is on the price list.
 *
 * One field, one boolean. An absent or `false` `priceList` is NOT on the list,
 * and after the backfill the absent case only occurs on a product created
 * before the field existed and never saved since.
 *
 * `addToPriceList` and `euPriceList` are no longer consulted here. Both are
 * retained as data — `addToPriceList` still records WHICH list a product was
 * coded for, which the checkbox cannot express — but neither decides
 * membership. Editing one of them no longer changes what the workbooks
 * contain; tick the box.
 */
// Since #1232 a market of `EU only` also keeps a ticked product off (see below).
export const isOnPriceList = (metadata) => metadata?.priceList === true && marketScope(metadata) !== 'EU only';
/**
 * Where a product may be sold (#1232): `metadata.market`. It narrows the two
 * list flags, never widens them: the US list needs the `priceList` tick AND a
 * market that is not EU only; the EU/UK lists need `euPriceList = Yes` AND a
 * market that is not US only. Blank, or anything unrecognised, is Global, so
 * a product nobody has scoped behaves exactly as it did before the field.
 */
export const MARKET_SCOPES = ['Global', 'US only', 'EU only'];
export function marketScope(metadata) {
    const v = metadata?.market;
    return MARKET_SCOPES.includes(v) ? v : 'Global';
}
/** On the EU/UK lists. Blank `euPriceList` is undecided, so this is equality with 'Yes'. */
export const isOnEuPriceList = (metadata) => metadata?.euPriceList === 'Yes' && marketScope(metadata) !== 'US only';
//# sourceMappingURL=price-list.js.map